import { Form } from 'antd';
import TrolleyLimitModal from 'components/Modals/TrolleyLimitModal';
import {
  Divider as SaleTypeDivider,
  Monetary as SaleTypePrice,
  PackingButton,
  Radio as SaleTypeRadio,
  SaleTypeGrid,
} from 'components/UI/ProductCard/molecules/ProductCardActions/ProductCardActions.styled';
import { PackingTypeSale } from 'enums/packingTypeSale';
import Packing from 'model/Packing';
import ProductOffer from 'model/ProductOffer';
import { Fragment } from 'react';
import {
  FormItem,
  PackagesInput,
} from 'screens/Products/components/BuyIntentionActions/styles';
import { BuyIntentionOfferPurchase } from 'screens/Products/components/BuyIntentionActions/useBuyIntentionOfferPurchase';
import { KeyPress } from 'utils/keypress';
import { formatDecimal } from 'utils/number';
import * as S from './offerStyles';

interface Props {
  product: ProductOffer;
  packingSelected: Packing;
  selectedTypeSale: PackingTypeSale;
  purchase: BuyIntentionOfferPurchase;
}

const SALE_TYPES = [
  PackingTypeSale.Packing,
  PackingTypeSale.Layer,
  PackingTypeSale.Trolley,
];

const SALE_TYPE_LABEL: Record<PackingTypeSale, string> = {
  [PackingTypeSale.Packing]: 'Embalagem',
  [PackingTypeSale.Layer]: 'Camada',
  [PackingTypeSale.Trolley]: 'Carrinho',
};

function priceBySaleType(product: ProductOffer, type: PackingTypeSale) {
  if (type === PackingTypeSale.Packing) return product.packagingPrice;
  if (type === PackingTypeSale.Layer) return product.layerPrice;
  return product.trolleyPrice;
}

function unitsBySaleType(packing: Packing, type: PackingTypeSale) {
  if (type === PackingTypeSale.Packing) return packing?.unitsPerPackage;
  if (type === PackingTypeSale.Layer) {
    return packing?.packagingByLayer * packing?.unitsPerPackage;
  }
  return (
    packing?.packagingByLayer *
    packing?.layersByTrolley *
    packing?.unitsPerPackage
  );
}

// Same price rule as ButtonPacking: special price once the minimum is reached,
// plus the packing cost when the packing has an additional cost.
function displayedPrice(
  product: ProductOffer,
  packing: Packing,
  type: PackingTypeSale,
  quantity: number,
) {
  const price = priceBySaleType(product, type);
  const units = quantity ? quantity * unitsBySaleType(packing, type) : 1;
  const current =
    quantity && product.specialPrice && units >= product.minimumQuantity
      ? product.specialPrice
      : price;
  return current > 0 && packing?.additionalCost
    ? current + product.packingCost
    : current;
}

export function OfferSaleTypes({
  product,
  packingSelected,
  selectedTypeSale,
  purchase,
}: Props) {
  return (
    <SaleTypeGrid role="radiogroup" aria-label="Tipo de venda">
      {SALE_TYPES.map((type) => {
        const isChecked = selectedTypeSale === type;
        const price = priceBySaleType(product, type);
        const isDisabled =
          !price ||
          !packingSelected[`maxAvailable${type}sQuantity` as keyof Packing];
        return (
          <Fragment key={type}>
            <PackingButton
              onClick={() => {
                if (!isDisabled) purchase.handleTypeSale(type);
              }}
              style={{
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              <SaleTypeRadio
                checked={isChecked}
                value={type}
                disabled={isDisabled}
                className="sale-type-label"
              >
                {SALE_TYPE_LABEL[type]}
              </SaleTypeRadio>
              <SaleTypePrice isChecked={isChecked}>
                {price > 0
                  ? `R$ ${formatDecimal(
                      displayedPrice(
                        product,
                        packingSelected,
                        type,
                        purchase.quantityPacking,
                      ),
                    )}`
                  : '-'}
              </SaleTypePrice>
            </PackingButton>
            {type !== PackingTypeSale.Trolley && <SaleTypeDivider />}
          </Fragment>
        );
      })}
    </SaleTypeGrid>
  );
}

export function OfferQuantity({
  product,
  purchase,
}: Pick<Props, 'product' | 'purchase'>) {
  return (
    <S.Quantity>
      <Form
        form={purchase.form}
        onFinish={purchase.onSubmit}
        onFinishFailed={purchase.onSubmitFailed}
      >
        <FormItem
          name="quantityPacking"
          rules={
            purchase.quantityPacking > 0
              ? purchase.formValidation.quantityPacking
              : undefined
          }
        >
          <PackagesInput
            id={`quantityPacking-${product.id}`}
            aria-label="Quantidade"
            onChange={({ target }) =>
              purchase.changeQuantity(Number(target.value))
            }
            autoComplete="off"
            onKeyPress={KeyPress.onlyNumber}
            addonAfter={purchase.textInputTypeSale}
            inputMode="numeric"
            type="tel"
          />
        </FormItem>
      </Form>
      <S.Hint>
        {`${purchase.purchaseInfo.qtdMinimaText} · Disponível: ${purchase.getMaxAvailable.max}`}
      </S.Hint>
      {purchase.purchaseInfo.totalPedidoText && (
        <S.Hint>{purchase.purchaseInfo.totalPedidoText}</S.Hint>
      )}
    </S.Quantity>
  );
}

export function OfferTrolleyLimit({
  selectedTypeSale,
  purchase,
}: Pick<Props, 'selectedTypeSale' | 'purchase'>) {
  return (
    <TrolleyLimitModal
      visible={purchase.showTrolleyModal}
      totalUnits={purchase.purchaseInfo.totalUnits}
      quantityOrdered={purchase.quantityPacking}
      selectedTypeSale={selectedTypeSale}
      unitsPerType={purchase.purchaseInfo.unitsPerType}
      totalPrice={purchase.totalPrice}
      shippingFeeTotal={
        purchase.hasShippingFee
          ? purchase.purchaseInfo.totalUnits * purchase.shippingFee
          : undefined
      }
      onConfirm={purchase.handleTrolleyConfirm}
      onCancel={() => purchase.setShowTrolleyModal(false)}
    />
  );
}
